import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import { AuthService, SharedService, getNotificationSocketConnection } from '@app/core';

export interface IUploadNotifyChip {
  companyId: string;
  shortName: string;
  name: string;
  count: number;
}

/**
 * Shared admin upload sticky-bar state + socket.
 * Kept outside list/review components so navigating into review does not drop the connection.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminUploadNotifyService implements OnDestroy {
  readonly chips$ = new BehaviorSubject<IUploadNotifyChip[]>([]);

  private socket: Socket | null = null;
  private byCompany = new Map<string, IUploadNotifyChip>();
  private started = false;

  constructor(
    private authService: AuthService,
    private sharedService: SharedService,
    private ngZone: NgZone
  ) {}

  /** Idempotent — safe to call from list and review ngOnInit. */
  ensureConnected(): void {
    if (this.started || !this.authService.user?.isAdmin || !this.authService.token) {
      return;
    }

    const connection = getNotificationSocketConnection(this.authService.token, []);
    if (!connection) {
      return;
    }

    this.started = true;
    this.socket = io(connection.url, connection.options);
    this.socket.on('admin_invoice_uploaded', (payload: {
      invoice_id?: string;
      company_id?: string;
      company_short_name?: string;
      company_name?: string;
    }) => {
      this.ngZone.run(() => this.onUploaded(payload));
    });
  }

  dismiss(): void {
    this.byCompany.clear();
    this.chips$.next([]);
  }

  ngOnDestroy(): void {
    if (this.socket) {
      this.socket.off('admin_invoice_uploaded');
      this.socket.disconnect();
      this.socket = null;
    }
    this.started = false;
  }

  private onUploaded(payload: {
    invoice_id?: string;
    company_id?: string;
    company_short_name?: string;
    company_name?: string;
  }): void {
    const companyId = payload?.company_id;
    if (!companyId) {
      return;
    }

    const shortName = (payload.company_short_name || '').trim()
      || this.sharedService.shortName(payload.company_name || '')
      || '—';
    const existing = this.byCompany.get(companyId);

    if (existing) {
      existing.count += 1;
      existing.shortName = shortName || existing.shortName;
      existing.name = payload.company_name || existing.name;
    } else {
      this.byCompany.set(companyId, {
        companyId,
        shortName,
        name: payload.company_name || '',
        count: 1
      });
    }

    this.chips$.next(Array.from(this.byCompany.values()));
  }
}
